<template>
    <div>
        <div class="card">
            <div class="card-header">
                <div class="col">
                    <h3 class="card-title">
                        <i class="ti ti-message"></i>
                        Assistentes de IA
                    </h3>

                    <p class="card-subtitle text-muted">Converse com agentes de IA a partir dos seus Flows</p>
                </div>
                <div class="col-auto">
                    <input
                        id="searchQueuesButton"
                        v-model="filter.query"
                        type="text"
                        class="form-control"
                        placeholder="Buscar assistentes por título"
                        @change="updateFilter"
                    />
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="!aiChatTriggerFlows.length && !filter.query" class="card-body">
                <div class="card-body">
                    <div class="text-center">
                        <div class="empty">
                            <p class="empty-title">
                                <i class="ti ti-message"></i> Nenhum assistente de IA registrado ainda
                            </p>
                            <p class="empty-subtitle text-muted">
                                Para criar um assistente de IA, você deve criar um Flow e adicionar um componente de
                                <strong>AI Chat Trigger</strong>.
                            </p>
                            <div class="empty-action">
                                <a href="/flows/new" target="_blank" class="btn btn-primary">
                                    <i class="icon ti ti-plus"></i>
                                    Criar novo Flow
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No Results State -->
            <div v-else-if="!aiChatTriggerFlows.length && filter.query" class="card-body">
                <div class="empty bg-muted-lt">
                    <p class="empty-title">Nenhum chat encontrado para esta busca.</p>
                </div>
            </div>

            <!-- Cards Section -->
            <div v-else-if="aiChatTriggerFlows.length" class="card-body">
                <div class="row row-deck row-cards">
                    <div v-for="flow in aiChatTriggerFlows" :key="flow._id" class="col-md-12 col-lg-6 col-xxl-4">
                        <div class="card card-sm">
                            <!-- Card Body -->
                            <div class="card-body p-3">
                                <div class="d-flex flex-column gap-2 h-100 justify-content-between">
                                    <!-- Icon and Title -->
                                    <div class="row align-items-center">
                                        <!-- Icon -->
                                        <div class="col-auto">
                                            <div class="bg-primary text-white avatar avatar-lg rounded-circle">
                                                <i class="fs-1 ti" :class="'ti-' + flow.icon"></i>
                                            </div>
                                        </div>

                                        <!-- Title and Info -->
                                        <div class="col">
                                            <h4
                                                class="h2 lh-sm title-clamp mb-1"
                                                data-bs-toggle="tooltip"
                                                :title="flow.title"
                                            >
                                                {{ flow.title }}
                                            </h4>
                                        </div>
                                    </div>

                                    <!-- Description -->
                                    <div class="row">
                                        <div class="col">
                                            <p
                                                class="text-muted paragraph-clamp"
                                                data-bs-toggle="tooltip"
                                                :title="flow.aiChatTriggerDescription"
                                            >
                                                {{ flow.aiChatTriggerDescription }}
                                            </p>
                                        </div>
                                    </div>

                                    <!-- Group -->
                                    <div class="row">
                                        <div class="col">
                                            <div class="d-flex gap-3">
                                                <span
                                                    class="badge bg-green-lt d-flex gap-1"
                                                    data-bs-toggle="tooltip"
                                                    :title="flow.toolNames.join('\n')"
                                                    ><i class="ti ti-info-circle fs-3"></i> Tools:
                                                    {{ flow.toolsCount }}
                                                </span>
                                                <span
                                                    :class="flow.group === null ? 'bg-gray-lt' : 'bg-blue-lt'"
                                                    class="badge d-flex"
                                                    >{{ flow.group || "Sem grupo" }}</span
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="card-footer px-3 text-end">
                                <a :href="'/chats/flow/' + flow._id" target="_blank" class="btn btn-primary py-2">
                                    <i class="icon ti ti-message"></i>
                                    Iniciar Chat
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="aiChatTriggerFlows.length > showPerPage" class="card-footer">
                <Pagination
                    v-if="total > showPerPage"
                    :key="paginationKey"
                    :show-per-page="showPerPage"
                    :current-page="currentPage"
                    :total="total"
                    @page-change="updatePage"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { store } from "../store"
import Api from "../services/api"
import moment from "moment"
import Pagination from "./Pagination"
import EventBus from "../services/event-bus"

export default {
    name: "ChatsList",
    components: {
        Pagination
    },
    data() {
        return {
            aiChatTriggerFlows: [],
            filter: {
                query: "",
                show_status: "enabled"
            },
            paginationKey: 0,
            showPerPage: 10,
            currentPage: 1,
            total: 0
        }
    },
    async mounted() {
        this.currentPage = parseInt(this.$route.query.page) || 1
        this.filter.query = this.$route.query.query || ""
        this.filter.group = localStorage.getItem("selectedGroup") || ""

        this.loadAiChatTriggerFlows()

        EventBus.on("update-selected-group", () => {
            this.filter.group = localStorage.getItem("selectedGroup") || ""
            this.updateFilter()
        })
    },
    methods: {
        async loadAiChatTriggerFlows() {
            this.paginationKey = Math.random()

            const response = await Api.flows.listAiChatFlows(this.currentPage, this.showPerPage, this.filter)

            this.aiChatTriggerFlows = this.mountFlowData(response.data.items)
            this.total = response.data.total
        },
        mountFlowData(flows) {
            const mountedFlows = flows.map((flow) => {
                return {
                    _id: flow._id,
                    flowName: flow.name,
                    group: this.groupName(flow.groupId),
                    createdAt: this.formatDate(flow.createdAt),
                    updatedAt: this.formatDate(flow.updatedAt),
                    aiChatTriggerDescription: flow.chatDescription || "Sem descrição",
                    title: flow.chatTitle || "Sem título",
                    icon: flow.chatIcon || "ti ti-cpu",
                    toolsCount: flow.toolsCount || 0,
                    toolNames: flow.toolNames || []
                }
            })

            return mountedFlows
        },
        formatDate(date) {
            return moment(date).format("DD/MM/YYYY HH:mm:ss")
        },
        async updatePage(page) {
            this.$router.replace({
                query: {
                    page: page,
                    query: this.filter.query,
                    _: Math.random()
                }
            })

            this.currentPage = page
        },
        updateFilter() {
            this.$router.replace({
                query: {
                    page: 1,
                    query: this.filter.query,
                    _: Math.random()
                }
            })
        },
        groupName(group_id) {
            const group = store.groups.find((item) => item._id === group_id)

            return group ? group.name : null
        }
    }
}
</script>

<style scoped>
.title-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.8;
    max-height: calc(1.8em * 2);
    padding-bottom: 0.1rem;
}

.paragraph-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
    max-height: calc(1.5em * 3);
}
</style>
